import { useMemo } from 'react';
import moment from 'moment';
import useDashboardExcelExport from '../common/export/useDashboardExcelExport';
import {
    useAvailableDateRange,
    useSalesOverallPerformanceData,
    useSalesPerformanceData,
    useStorePerformanceData
} from './hooks';
import { CATEGORY, SHORTEN_SALES_COMPANY } from './constants';
import { useIsComponentUsable } from '../common/providers/blacklistedComponentsProvider/hooks';
import {
    COMPONENT_TOPLINE_PERFORMANCE,
    COMPONENT_TOPLINE_PERFORMANCE_MTD_SALES,
    COMPONENT_TOPLINE_PERFORMANCE_MTD_TARGET,
    COMPONENT_TOPLINE_PERFORMANCE_MTD_VS_TARGET,
    COMPONENT_TOPLINE_PERFORMANCE_MTD_VS_LY,
    COMPONENT_TOPLINE_PERFORMANCE_YTD_SALES,
    COMPONENT_TOPLINE_PERFORMANCE_YTD_TARGET,
    COMPONENT_TOPLINE_PERFORMANCE_YTD_VS_TARGET,
    COMPONENT_TOPLINE_PERFORMANCE_YTD_VS_LY,
    COMPONENT_OVERALL_PERFORMANCE,
    COMPONENT_OVERALL_PERFORMANCE_COMPANY,
    COMPONENT_OVERALL_PERFORMANCE_SALES,
    COMPONENT_OVERALL_PERFORMANCE_CONTRIBUTION,
    COMPONENT_OVERALL_PERFORMANCE_TARGET_SALES,
    COMPONENT_OVERALL_PERFORMANCE_VS_TARGET,
    COMPONENT_OVERALL_PERFORMANCE_LY_SALES,
    COMPONENT_OVERALL_PERFORMANCE_VS_LY_SALES,
    COMPONENT_PERFORMANCE_PER_GROUP,
    COMPONENT_PERFORMANCE_PER_GROUP_LABEL,
    COMPONENT_PERFORMANCE_PER_GROUP_SALES,
    COMPONENT_PERFORMANCE_PER_GROUP_CONTRIBUTION,
    COMPONENT_PERFORMANCE_PER_GROUP_TARGET_SALES,
    COMPONENT_PERFORMANCE_PER_GROUP_VS_TARGET,
    COMPONENT_PERFORMANCE_PER_GROUP_LY_SALES,
    COMPONENT_PERFORMANCE_PER_GROUP_VS_LY_SALES,
    COMPONENT_STORE_PERFORMANCE,
    COMPONENT_STORE_PERFORMANCE_STORE,
    COMPONENT_STORE_PERFORMANCE_ADS,
    COMPONENT_STORE_PERFORMANCE_MTD_YTD_SALES,
    COMPONENT_STORE_PERFORMANCE_MTD_YTD_TARGET,
    COMPONENT_STORE_PERFORMANCE_VS_TARGET,
    COMPONENT_STORE_PERFORMANCE_LY_MTD_YTD_SALES,
    COMPONENT_STORE_PERFORMANCE_VS_LY_SALES,
    COMPONENT_STORE_PERFORMANCE_SALES_PER_SQM,
    COMPONENT_STORE_PERFORMANCE_VS_LY_SALES_PER_SQM
} from './dashboardDefinition';
import { buildSectionsFromConfig } from '../common/export/exportConfigUtils';

const PRODUCT_CAT_COLS = ['888', '888 (Outright)', '4k'];
const SHEET_TAB_COLORS = {
    summary: '1F4E78',
    storeDetail: '2E7D32',
    perDimension: '4472C4'
};

function buildMonthDisplay(months = []) {
    if (!months?.length) {
        return 'N/A';
    }

    const minMonth = Math.min(...months) - 1;
    const maxMonth = Math.max(...months) - 1;

    if (months.length === 1) {
        return moment().month(minMonth).format('MMM');
    }

    return `${moment().month(minMonth).format('MMM')}-${moment().month(maxMonth).format('MMM')}`;
}

function formatFiltersForMetadata(filters, period, dataView, refreshSchedule, dataAsOf) {
    return {
        CompanyIds: (filters?.company_id || []).join(', '),
        Month: buildMonthDisplay(filters?.month),
        Year: filters?.year || 'N/A',
        Period: (period || 'mtd').toUpperCase(),
        DataView: dataView || 'total',
        DataAsOf: dataAsOf || 'N/A',
        LocalFiltersPolicy: 'Store/metric local filters are UI-only and are not applied in export.',
        RefreshSchedule: (refreshSchedule || []).join(', '),
        ExportedAt: moment().format('YYYY-MM-DD HH:mm:ss')
    };
}

export default function useSalesDashboardExcelExport({
    filters,
    params,
    period,
    dataView,
    selectedRows,
    refreshSchedule
}) {
    const { data: availableDateRange, isLoading: isDateRangeLoading } = useAvailableDateRange();
    const isMTD = period === 'mtd';
    const paramsBasedOnPeriod = isMTD ? params : { ...params, month: [1, ...(params?.month || [])] };

    const { actualSales, targetSales, lastYearSales, dailyTargetSales, isLoading: isToplineMtdLoading } =
        useSalesOverallPerformanceData({ params });
    const {
        actualSales: ytdActualSales,
        targetSales: ytdTargetSales,
        lastYearSales: ytdLastYearSales,
        dailyTargetSales: ytdDailyTargetSales,
        isLoading: isToplineYtdLoading
    } = useSalesOverallPerformanceData({ params: { ...params, month: [1, ...(params?.month || [])] } });

    const { data: overallData, isLoading: isOverallLoading } = useSalesPerformanceData({
        params: {
            ...paramsBasedOnPeriod,
            group_by: ['company_id', 'company'].join(',')
        }
    });

    const { data: perGroupData, isLoading: isPerGroupLoading } = useSalesPerformanceData({
        params: {
            ...paramsBasedOnPeriod,
            group_by: filters?.group
        }
    });

    const isProductGroup = filters?.group === 'product_group';
    const { data: storeData, isLoading: isStoreLoading } = useStorePerformanceData({
        params: {
            ...paramsBasedOnPeriod,
            group_by: 'branch_id,store,mall_type,store_size,mall_category,region',
            limit: 100000,
            product_group: isProductGroup ? selectedRows : null
        }
    });

    // Fetch complete data for each grouping dimension for export
    const { data: perProductGroupData, isLoading: isPerProductGroupLoading } = useSalesPerformanceData({
        params: {
            ...paramsBasedOnPeriod,
            group_by: 'product_group'
        }
    });

    const { data: perMallTypeData, isLoading: isPerMallTypeLoading } = useSalesPerformanceData({
        params: {
            ...paramsBasedOnPeriod,
            group_by: 'mall_type'
        }
    });

    const { data: perStoreSizeData, isLoading: isPerStoreSizeLoading } = useSalesPerformanceData({
        params: {
            ...paramsBasedOnPeriod,
            group_by: 'store_size'
        }
    });

    const { data: perMallCategoryData, isLoading: isPerMallCategoryLoading } = useSalesPerformanceData({
        params: {
            ...paramsBasedOnPeriod,
            group_by: 'mall_category'
        }
    });

    const { data: perRegionData, isLoading: isPerRegionLoading } = useSalesPerformanceData({
        params: {
            ...paramsBasedOnPeriod,
            group_by: 'region'
        }
    });

    // Call all hooks unconditionally at top level (React Hook Rules)
    // Section visibility hooks
    const toplineSectionUsable = useIsComponentUsable(COMPONENT_TOPLINE_PERFORMANCE);
    const overallSectionUsable = useIsComponentUsable(COMPONENT_OVERALL_PERFORMANCE);
    const perGroupSectionUsable = useIsComponentUsable(COMPONENT_PERFORMANCE_PER_GROUP);
    const storeSectionUsable = useIsComponentUsable(COMPONENT_STORE_PERFORMANCE);

    // Topline column visibility hooks
    // Call each hook unconditionally, then combine results
    const toplineMtdSalesUsable = useIsComponentUsable(COMPONENT_TOPLINE_PERFORMANCE_MTD_SALES);
    const toplineYtdSalesUsable = useIsComponentUsable(COMPONENT_TOPLINE_PERFORMANCE_YTD_SALES);
    const toplineSalesUsable = toplineMtdSalesUsable || toplineYtdSalesUsable;

    const toplineMtdTargetUsable = useIsComponentUsable(COMPONENT_TOPLINE_PERFORMANCE_MTD_TARGET);
    const toplineYtdTargetUsable = useIsComponentUsable(COMPONENT_TOPLINE_PERFORMANCE_YTD_TARGET);
    const toplineTargetUsable = toplineMtdTargetUsable || toplineYtdTargetUsable;

    const toplineMtdVsTargetUsable = useIsComponentUsable(COMPONENT_TOPLINE_PERFORMANCE_MTD_VS_TARGET);
    const toplineYtdVsTargetUsable = useIsComponentUsable(COMPONENT_TOPLINE_PERFORMANCE_YTD_VS_TARGET);
    const toplineMtdVsTargetUsableResult = toplineMtdVsTargetUsable || toplineYtdVsTargetUsable;

    const toplineMtdLyUsable = useIsComponentUsable(COMPONENT_TOPLINE_PERFORMANCE_MTD_VS_LY);
    const toplineYtdLyUsable = useIsComponentUsable(COMPONENT_TOPLINE_PERFORMANCE_YTD_VS_LY);
    const toplineLyUsable = toplineMtdLyUsable || toplineYtdLyUsable;

    // Overall column visibility hooks
    const overallCompanyUsable = useIsComponentUsable(COMPONENT_OVERALL_PERFORMANCE_COMPANY);
    const overallSalesUsable = useIsComponentUsable(COMPONENT_OVERALL_PERFORMANCE_SALES);
    const overallContributionUsable = useIsComponentUsable(COMPONENT_OVERALL_PERFORMANCE_CONTRIBUTION);
    const overallTargetUsable = useIsComponentUsable(COMPONENT_OVERALL_PERFORMANCE_TARGET_SALES);
    const overallVsTargetUsable = useIsComponentUsable(COMPONENT_OVERALL_PERFORMANCE_VS_TARGET);
    const overallLyUsable = useIsComponentUsable(COMPONENT_OVERALL_PERFORMANCE_LY_SALES);
    const overallVsLyUsable = useIsComponentUsable(COMPONENT_OVERALL_PERFORMANCE_VS_LY_SALES);

    // Per Group column visibility hooks
    const perGroupLabelUsable = useIsComponentUsable(COMPONENT_PERFORMANCE_PER_GROUP_LABEL);
    const perGroupSalesUsable = useIsComponentUsable(COMPONENT_PERFORMANCE_PER_GROUP_SALES);
    const perGroupContributionUsable = useIsComponentUsable(COMPONENT_PERFORMANCE_PER_GROUP_CONTRIBUTION);
    const perGroupTargetUsable = useIsComponentUsable(COMPONENT_PERFORMANCE_PER_GROUP_TARGET_SALES);
    const perGroupVsTargetUsable = useIsComponentUsable(COMPONENT_PERFORMANCE_PER_GROUP_VS_TARGET);
    const perGroupLyUsable = useIsComponentUsable(COMPONENT_PERFORMANCE_PER_GROUP_LY_SALES);
    const perGroupVsLyUsable = useIsComponentUsable(COMPONENT_PERFORMANCE_PER_GROUP_VS_LY_SALES);

    // Store column visibility hooks
    const storeStoreUsable = useIsComponentUsable(COMPONENT_STORE_PERFORMANCE_STORE);
    const storeAdsUsable = useIsComponentUsable(COMPONENT_STORE_PERFORMANCE_ADS);
    const storeSalesUsable = useIsComponentUsable(COMPONENT_STORE_PERFORMANCE_MTD_YTD_SALES);
    const storeTargetUsable = useIsComponentUsable(COMPONENT_STORE_PERFORMANCE_MTD_YTD_TARGET);
    const storeVsTargetUsable = useIsComponentUsable(COMPONENT_STORE_PERFORMANCE_VS_TARGET);
    const storeLyUsable = useIsComponentUsable(COMPONENT_STORE_PERFORMANCE_LY_MTD_YTD_SALES);
    const storeVsLyUsable = useIsComponentUsable(COMPONENT_STORE_PERFORMANCE_VS_LY_SALES);
    const storeSalesPerSqmUsable = useIsComponentUsable(COMPONENT_STORE_PERFORMANCE_SALES_PER_SQM);
    const storeVsLySqmUsable = useIsComponentUsable(COMPONENT_STORE_PERFORMANCE_VS_LY_SALES_PER_SQM);

    // Organize into objects after all hooks are called
    const sectionVisibility = {
        topline: toplineSectionUsable,
        overall: overallSectionUsable,
        perGroup: perGroupSectionUsable,
        store: storeSectionUsable
    };

    const columnVisibility = {
        topline: {
            sales: toplineSalesUsable,
            targetSales: toplineTargetUsable,
            vsRunningDailyTarget: toplineMtdVsTargetUsableResult,
            lastYearSales: toplineLyUsable,
            vsLastYear: toplineLyUsable
        },
        overall: {
            company: overallCompanyUsable,
            sales: overallSalesUsable,
            contribution: overallContributionUsable,
            targetSales: overallTargetUsable,
            runningDailyTarget: overallVsTargetUsable,
            vsRunningDailyTarget: overallVsTargetUsable,
            lastYearSales: overallLyUsable,
            vsLastYear: overallVsLyUsable
        },
        perGroup: {
            group: perGroupLabelUsable,
            sales: perGroupSalesUsable,
            contribution: perGroupContributionUsable,
            targetSales: perGroupTargetUsable,
            runningDailyTarget: perGroupVsTargetUsable,
            vsRunningDailyTarget: perGroupVsTargetUsable,
            lastYearSales: perGroupLyUsable,
            vsLastYear: perGroupVsLyUsable,
            isTopCategory: perGroupLabelUsable
        },
        store: {
            store: storeStoreUsable,
            ads: storeAdsUsable,
            sales: storeSalesUsable,
            targetSales: storeTargetUsable,
            runningDailyTarget: storeVsTargetUsable,
            vsRunningDailyTarget: storeVsTargetUsable,
            lastYearSales: storeLyUsable,
            vsLastYear: storeVsLyUsable,
            salesPerSqm: storeSalesPerSqmUsable,
            vsLastYearSqm: storeVsLySqmUsable
        }
    };

    const formattedOverallData = useMemo(() => {
        return (overallData || []).map((row) => ({
            company: SHORTEN_SALES_COMPANY[row.company_id] || row.company_id,
            sales: row.actual_sales,
            contribution: row.percent_contribution,
            targetSales: row.target_sales,
            runningDailyTarget: row.daily_target_sales,
            vsRunningDailyTarget: row.daily_target_sales ? (row.actual_sales / row.daily_target_sales) * 100 : null,
            lastYearSales: row.last_year_sales,
            vsLastYear: row.last_year_sales ? (row.actual_sales / row.last_year_sales) * 100 : null
        }));
    }, [overallData]);

    const formattedProductGroupData = useMemo(() => {
        const filtered = (perGroupData || []).filter((row) => row.product_group !== 'Others' && row.product_group !== 'others');

        return filtered.map((row) => ({
            group: row[filters?.group],
            sales: row.actual_sales,
            contribution: row.percent_contribution,
            targetSales: row.target_sales,
            runningDailyTarget: row.daily_target_sales,
            vsRunningDailyTarget: row.daily_target_sales ? (row.actual_sales / row.daily_target_sales) * 100 : null,
            lastYearSales: row.last_year_sales,
            vsLastYear: row.last_year_sales ? (row.actual_sales / row.last_year_sales) * 100 : null,
            isTopCategory: PRODUCT_CAT_COLS.includes(row.product_group)
        }));
    }, [filters?.group, perGroupData]);

    const formattedStoreData = useMemo(() => {
        return (storeData || []).map((row) => ({
            branchId: row.branch_id,
            store: row.store,
            ads: row.ads,
            sales: row.actual_sales,
            targetSales: row.target_sales,
            runningDailyTarget: row.daily_target_sales,
            vsRunningDailyTarget: row.vs_running_daily_target,
            lastYearSales: row.last_year_actual_sales,
            vsLastYear: row.vs_last_year_mtd_ytd_sales,
            salesPerSqm: row.sales_per_sqm,
            vsLastYearSqm: row.vs_last_year_sqm_sales
        }));
    }, [storeData]);

    const formattedMallTypeData = useMemo(() => {
        return (perMallTypeData || []).map((row) => ({
            group: row.mall_type,
            sales: row.actual_sales,
            contribution: row.percent_contribution,
            targetSales: row.target_sales,
            runningDailyTarget: row.daily_target_sales,
            vsRunningDailyTarget: row.daily_target_sales ? (row.actual_sales / row.daily_target_sales) * 100 : null,
            lastYearSales: row.last_year_sales,
            vsLastYear: row.last_year_sales ? (row.actual_sales / row.last_year_sales) * 100 : null
        }));
    }, [perMallTypeData]);

    const formattedStoreSizeData = useMemo(() => {
        return (perStoreSizeData || []).map((row) => ({
            group: row.store_size,
            sales: row.actual_sales,
            contribution: row.percent_contribution,
            targetSales: row.target_sales,
            runningDailyTarget: row.daily_target_sales,
            vsRunningDailyTarget: row.daily_target_sales ? (row.actual_sales / row.daily_target_sales) * 100 : null,
            lastYearSales: row.last_year_sales,
            vsLastYear: row.last_year_sales ? (row.actual_sales / row.last_year_sales) * 100 : null
        }));
    }, [perStoreSizeData]);

    const formattedMallCategoryData = useMemo(() => {
        return (perMallCategoryData || []).map((row) => ({
            group: row.mall_category,
            sales: row.actual_sales,
            contribution: row.percent_contribution,
            targetSales: row.target_sales,
            runningDailyTarget: row.daily_target_sales,
            vsRunningDailyTarget: row.daily_target_sales ? (row.actual_sales / row.daily_target_sales) * 100 : null,
            lastYearSales: row.last_year_sales,
            vsLastYear: row.last_year_sales ? (row.actual_sales / row.last_year_sales) * 100 : null
        }));
    }, [perMallCategoryData]);

    const formattedRegionData = useMemo(() => {
        return (perRegionData || []).map((row) => ({
            group: row.region,
            sales: row.actual_sales,
            contribution: row.percent_contribution,
            targetSales: row.target_sales,
            runningDailyTarget: row.daily_target_sales,
            vsRunningDailyTarget: row.daily_target_sales ? (row.actual_sales / row.daily_target_sales) * 100 : null,
            lastYearSales: row.last_year_sales,
            vsLastYear: row.last_year_sales ? (row.actual_sales / row.last_year_sales) * 100 : null
        }));
    }, [perRegionData]);

    const sections = useMemo(() => {
        const monthDisplay = buildMonthDisplay(filters?.month);
        const yearDisplay = filters?.year || 'N/A';
        const periodLabel = isMTD ? 'MTD' : 'YTD';

        return buildSectionsFromConfig([
            {
                enabled: sectionVisibility.topline,
                sheetName: 'Topline',
                tabColor: SHEET_TAB_COLORS.summary,
                title: 'Topline Performance',
                description: 'Snapshot of MTD and YTD topline metrics.',
                tooltips: [
                    { label: 'vs Running Daily Target', text: 'Actual sales divided by running daily target, expressed as percentage.' },
                    { label: 'vs Last Year', text: 'Actual sales divided by last year sales for the same date coverage.' }
                ],
                columnVisibility: columnVisibility.topline,
                columns: [
                    { key: 'view', label: 'View' },
                    { key: 'coverage', label: 'Coverage' },
                    { key: 'sales', label: 'Sales', format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'targetSales', label: 'Target Sales', format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'runningDailyTarget', label: 'Running Daily Target', format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsRunningDailyTarget', label: 'vs Running Daily Target (%)', format: { type: 'percent', decimals: 2 } },
                    { key: 'lastYearSales', label: 'Last Year Sales', format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsLastYear', label: 'vs Last Year (%)', format: { type: 'percent', decimals: 2 } }
                ],
                rows: [
                    {
                        view: 'MTD',
                        coverage: `${monthDisplay} ${yearDisplay}`,
                        sales: actualSales,
                        targetSales,
                        runningDailyTarget: dailyTargetSales,
                        vsRunningDailyTarget: dailyTargetSales ? (actualSales / dailyTargetSales) * 100 : null,
                        lastYearSales,
                        vsLastYear: lastYearSales ? (actualSales / lastYearSales) * 100 : null
                    },
                    {
                        view: 'YTD',
                        coverage: `Jan-${monthDisplay.split('-').slice(-1)[0]} ${yearDisplay}`,
                        sales: ytdActualSales,
                        targetSales: ytdTargetSales,
                        runningDailyTarget: ytdDailyTargetSales,
                        vsRunningDailyTarget: ytdDailyTargetSales ? (ytdActualSales / ytdDailyTargetSales) * 100 : null,
                        lastYearSales: ytdLastYearSales,
                        vsLastYear: ytdLastYearSales ? (ytdActualSales / ytdLastYearSales) * 100 : null
                    }
                ]
            },
            {
                enabled: sectionVisibility.overall,
                sheetName: 'Overall',
                tabColor: SHEET_TAB_COLORS.summary,
                title: 'Overall Performance',
                description: `Company-level ${periodLabel} performance table currently shown in dashboard.`,
                tooltips: [
                    { label: '% cont.', text: 'Percent contribution to total sales in the selected period.' }
                ],
                columnVisibility: columnVisibility.overall,
                columns: [
                    { key: 'company', label: 'Company' },
                    { key: 'sales', label: `${periodLabel} Sales`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'contribution', label: '% Contribution', format: { type: 'percent', decimals: 2 } },
                    { key: 'targetSales', label: `${periodLabel} Target`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'runningDailyTarget', label: 'Running Daily Target', format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsRunningDailyTarget', label: 'vs Running Daily Target (%)', format: { type: 'percent', decimals: 2 } },
                    { key: 'lastYearSales', label: `Last Year ${periodLabel} Sales`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsLastYear', label: 'vs Last Year (%)', format: { type: 'percent', decimals: 2 } }
                ],
                rows: formattedOverallData
            },
            {
                enabled: sectionVisibility.perGroup,
                sheetName: 'Per Product Group',
                title: 'Performance Per Product Group',
                description: `All product categories performance for ${periodLabel}.`,
                tooltips: [
                    { label: 'Top Category', text: 'True means category belongs to 888 / 888 (Outright) / 4k bucket.' }
                ],
                tabColor: SHEET_TAB_COLORS.perDimension,
                columnVisibility: columnVisibility.perGroup,
                columns: [
                    { key: 'group', label: 'Product Group' },
                    { key: 'sales', label: `${periodLabel} Sales`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'contribution', label: '% Contribution', format: { type: 'percent', decimals: 2 } },
                    { key: 'targetSales', label: `${periodLabel} Target`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'runningDailyTarget', label: 'Running Daily Target', format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsRunningDailyTarget', label: 'vs Running Daily Target (%)', format: { type: 'percent', decimals: 2 } },
                    { key: 'lastYearSales', label: `Last Year ${periodLabel} Sales`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsLastYear', label: 'vs Last Year (%)', format: { type: 'percent', decimals: 2 } },
                    { key: 'isTopCategory', label: 'Top Category' }
                ],
                rows: formattedProductGroupData
            },
            {
                enabled: sectionVisibility.perGroup,
                sheetName: 'Per Mall Type',
                title: 'Performance Per Mall Type',
                description: `All mall types performance for ${periodLabel}.`,
                tabColor: SHEET_TAB_COLORS.perDimension,
                columnVisibility: columnVisibility.perGroup,
                columns: [
                    { key: 'group', label: 'Mall Type' },
                    { key: 'sales', label: `${periodLabel} Sales`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'contribution', label: '% Contribution', format: { type: 'percent', decimals: 2 } },
                    { key: 'targetSales', label: `${periodLabel} Target`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'runningDailyTarget', label: 'Running Daily Target', format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsRunningDailyTarget', label: 'vs Running Daily Target (%)', format: { type: 'percent', decimals: 2 } },
                    { key: 'lastYearSales', label: `Last Year ${periodLabel} Sales`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsLastYear', label: 'vs Last Year (%)', format: { type: 'percent', decimals: 2 } }
                ],
                rows: formattedMallTypeData
            },
            {
                enabled: sectionVisibility.perGroup,
                sheetName: 'Per Store Size',
                title: 'Performance Per Store Size',
                description: `All store sizes performance for ${periodLabel}.`,
                tabColor: SHEET_TAB_COLORS.perDimension,
                columnVisibility: columnVisibility.perGroup,
                columns: [
                    { key: 'group', label: 'Store Size' },
                    { key: 'sales', label: `${periodLabel} Sales`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'contribution', label: '% Contribution', format: { type: 'percent', decimals: 2 } },
                    { key: 'targetSales', label: `${periodLabel} Target`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'runningDailyTarget', label: 'Running Daily Target', format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsRunningDailyTarget', label: 'vs Running Daily Target (%)', format: { type: 'percent', decimals: 2 } },
                    { key: 'lastYearSales', label: `Last Year ${periodLabel} Sales`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsLastYear', label: 'vs Last Year (%)', format: { type: 'percent', decimals: 2 } }
                ],
                rows: formattedStoreSizeData
            },
            {
                enabled: sectionVisibility.perGroup,
                sheetName: 'Per Mall Category',
                title: 'Performance Per Mall Category',
                description: `All mall categories performance for ${periodLabel}.`,
                tabColor: SHEET_TAB_COLORS.perDimension,
                columnVisibility: columnVisibility.perGroup,
                columns: [
                    { key: 'group', label: 'Mall Category' },
                    { key: 'sales', label: `${periodLabel} Sales`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'contribution', label: '% Contribution', format: { type: 'percent', decimals: 2 } },
                    { key: 'targetSales', label: `${periodLabel} Target`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'runningDailyTarget', label: 'Running Daily Target', format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsRunningDailyTarget', label: 'vs Running Daily Target (%)', format: { type: 'percent', decimals: 2 } },
                    { key: 'lastYearSales', label: `Last Year ${periodLabel} Sales`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsLastYear', label: 'vs Last Year (%)', format: { type: 'percent', decimals: 2 } }
                ],
                rows: formattedMallCategoryData
            },
            {
                enabled: sectionVisibility.perGroup,
                sheetName: 'Per Region',
                title: 'Performance Per Region',
                description: `All regions performance for ${periodLabel}.`,
                tabColor: SHEET_TAB_COLORS.perDimension,
                columnVisibility: columnVisibility.perGroup,
                columns: [
                    { key: 'group', label: 'Region' },
                    { key: 'sales', label: `${periodLabel} Sales`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'contribution', label: '% Contribution', format: { type: 'percent', decimals: 2 } },
                    { key: 'targetSales', label: `${periodLabel} Target`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'runningDailyTarget', label: 'Running Daily Target', format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsRunningDailyTarget', label: 'vs Running Daily Target (%)', format: { type: 'percent', decimals: 2 } },
                    { key: 'lastYearSales', label: `Last Year ${periodLabel} Sales`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsLastYear', label: 'vs Last Year (%)', format: { type: 'percent', decimals: 2 } }
                ],
                rows: formattedRegionData
            },
            {
                enabled: sectionVisibility.store,
                sheetName: 'Store',
                tabColor: SHEET_TAB_COLORS.storeDetail,
                title: 'Store Performance',
                description: `Store-level ${periodLabel} performance table currently loaded in dashboard.`,
                tooltips: [
                    { label: 'ADS', text: 'Average Daily Sales.' },
                    { label: 'Sales per SQM', text: 'Sales normalized by selling floor area and date coverage.' }
                ],
                columnVisibility: columnVisibility.store,
                columns: [
                    { key: 'store', label: 'Store' },
                    { key: 'ads', label: 'ADS', format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'sales', label: `${periodLabel} Sales`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'targetSales', label: `${periodLabel} Target`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'runningDailyTarget', label: 'Running Daily Target', format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsRunningDailyTarget', label: 'vs Running Daily Target (%)', format: { type: 'percent', decimals: 2 } },
                    { key: 'lastYearSales', label: `Last Year ${periodLabel}`, format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsLastYear', label: 'vs Last Year (%)', format: { type: 'percent', decimals: 2 } },
                    { key: 'salesPerSqm', label: 'Sales per SQM', format: { type: 'currency', currencySymbol: '₱', decimals: 2 } },
                    { key: 'vsLastYearSqm', label: 'vs Last Year SQM (%)', format: { type: 'percent', decimals: 2 } }
                ],
                rows: formattedStoreData
            }
        ]);
    }, [
        actualSales,
        columnVisibility.overall,
        columnVisibility.perGroup,
        columnVisibility.store,
        columnVisibility.topline,
        dailyTargetSales,
        filters?.group,
        filters?.month,
        filters?.year,
        formattedOverallData,
        formattedProductGroupData,
        formattedMallTypeData,
        formattedStoreSizeData,
        formattedMallCategoryData,
        formattedRegionData,
        formattedStoreData,
        isMTD,
        lastYearSales,
        sectionVisibility.overall,
        sectionVisibility.perGroup,
        sectionVisibility.store,
        sectionVisibility.topline,
        targetSales,
        ytdActualSales,
        ytdDailyTargetSales,
        ytdLastYearSales,
        ytdTargetSales
    ]);

    const metadata = useMemo(() => {
        const dataAsOf = availableDateRange?.max ? moment(availableDateRange.max).format('MMMM DD, YYYY') : 'N/A';
        return formatFiltersForMetadata(filters, period, dataView, refreshSchedule, dataAsOf);
    }, [availableDateRange?.max, dataView, filters, period, refreshSchedule]);

    const isLoading = isDateRangeLoading || isToplineMtdLoading || isToplineYtdLoading || isOverallLoading || isPerGroupLoading || isPerProductGroupLoading || isPerMallTypeLoading || isPerStoreSizeLoading || isPerMallCategoryLoading || isPerRegionLoading || isStoreLoading;

    const exportHandler = useDashboardExcelExport({
        fileName: `markets-sales-dashboard-${moment().format('YYYYMMDD-HHmmss')}`,
        sections,
        metadata,
        disabled: isLoading,
        disabledMessage: 'Dashboard data is still loading. Please try exporting again in a moment.',
        beforeExportMessage: 'Preparing full Excel export (all visible metrics, global filters applied)...',
        successMessage: `Excel export ready with ${sections.length} sheet(s).`
    });

    return {
        ...exportHandler,
        isLoading
    };
}









